import { faFile, faFilePdf } from "@fortawesome/pro-light-svg-icons";
import { de, enUS, it } from "date-fns/locale";
import { CollectionType, PasswordPolicyError } from "./types";
import {
  File as ApiFile,
  Folder,
  LanguageLocaleEnum,
  PasswordPolicy,
  User,
} from "@/api/openapi";
import { AxiosResponse } from "axios";

type SetCookieConsentStorageParams = {
  essentialOnly: boolean;
};

export function setCookieConsentStorage(
  preferences: SetCookieConsentStorageParams | null,
) {
  if (!preferences) {
    localStorage.removeItem("cookieConsent");
    return;
  }

  const essentialOnly = preferences.essentialOnly;
  localStorage.setItem(
    "cookieConsent",
    essentialOnly ? "essentialOnly" : "all",
  );
}

export function getCookieConsent(): { essentialOnly: boolean } | null {
  const consentString = localStorage.getItem("cookieConsent");
  if (consentString === "essentialOnly") {
    return { essentialOnly: true };
  } else if (consentString === "all") {
    return { essentialOnly: false };
  }

  return null;
}

export function shouldShowCookieConsentBanner() {
  const cookieConsent = getCookieConsent();
  if (cookieConsent === null) {
    return true;
  }

  return false;
}

export function getAccessToken(): string | null {
  return localStorage.getItem("accessToken");
}

export function setAccessTokenStorage(accessToken: string | null) {
  if (accessToken) localStorage.setItem("accessToken", accessToken);
  else localStorage.removeItem("accessToken");
}

export function getRefreshToken(): string | null {
  return localStorage.getItem("refreshToken");
}

export function setRefreshTokenStorage(refreshToken: string | null) {
  if (refreshToken) localStorage.setItem("refreshToken", refreshToken);
  else localStorage.removeItem("refreshToken");
}

export function getPreAuthToken(): string | null {
  return localStorage.getItem("preAuthToken");
}

export function setPreAuthTokenStorage(preAuthToken: string | null) {
  if (preAuthToken) localStorage.setItem("preAuthToken", preAuthToken);
  else localStorage.removeItem("preAuthToken");
}

export function setPasswordExpiredTokenStorage(
  passwordExpiredToken: string | null,
) {
  if (passwordExpiredToken)
    localStorage.setItem("passwordExpiredToken", passwordExpiredToken);
  else localStorage.removeItem("passwordExpiredToken");
}

export function getPasswordExpiredToken() {
  return localStorage.getItem("passwordExpiredToken");
}

// non-null while the active session is an impersonation session; holds the
// admin user's own id (AuthResponse.actorId)
export function getActorId(): string | null {
  return localStorage.getItem("actorId");
}

export function setActorIdStorage(actorId: string | null | undefined) {
  if (actorId) localStorage.setItem("actorId", actorId);
  else localStorage.removeItem("actorId");
}

export function setSsoRedirectToStorage(redirectTo: string | null) {
  if (redirectTo) localStorage.setItem("ssoRedirectTo", redirectTo);
  else localStorage.removeItem("ssoRedirectTo");
}

export function getSsoRedirectToStorage(): string | null {
  return localStorage.getItem("ssoRedirectTo");
}

export type AdminSessionStash = {
  accessToken: string;
  refreshToken: string;
  user: User;
};

// the admin's own session, stashed away while impersonating so it can be
// restored on "return to admin"
export function getAdminSessionStash(): AdminSessionStash | null {
  const raw = localStorage.getItem("adminSessionStash");
  if (!raw) return null;

  try {
    return JSON.parse(raw) as AdminSessionStash;
  } catch {
    return null;
  }
}

export function setAdminSessionStash(stash: AdminSessionStash | null) {
  if (stash) localStorage.setItem("adminSessionStash", JSON.stringify(stash));
  else localStorage.removeItem("adminSessionStash");
}

export function getSelectedMandateId() {
  return localStorage.getItem("selectedMandate");
}

export function setSelectedMandateIdStorage(mandateId: string | null) {
  // return localStorage.removeItem("selectedMandate");

  if (mandateId) localStorage.setItem("selectedMandate", mandateId);
  else localStorage.removeItem("selectedMandate");
}

export function beautifyBytes(bytes: number) {
  if (bytes > 1e15) {
    // lel
    return `${(bytes / 1e15).toFixed(1)} PB`;
  }
  if (bytes > 1e12) {
    return `${(bytes / 1e12).toFixed(1)} TB`;
  }
  if (bytes > 1e9) {
    return `${(bytes / 1e9).toFixed(1)} GB`;
  }
  if (bytes > 1e6) {
    return `${(bytes / 1e6).toFixed(1)} MB`;
  }
  if (bytes > 1e3) {
    return `${(bytes / 1e3).toFixed(1)} KB`;
  }
  return `${bytes} B`;
}
export function iterablesEqualById<T extends CollectionType>(
  a?: Iterable<T>,
  b?: Iterable<T>,
): boolean {
  // if one of them is undefined, it's enough to check whether they're equal
  if (a === undefined || b === undefined) {
    return a === b;
  }

  // if they're arrays and the sizes don't match they can't be equal
  if (a instanceof Array && b instanceof Array && a.length !== b.length)
    return false;

  // put all ids of 'a' in a set
  const keysA = new Set<string>();
  for (const item of a) keysA.add(item.id);

  // put all ids of 'b' in a set
  const keysB = new Set<string>();
  for (const item of b) keysB.add(item.id);

  // if they don't have the same size, they can't match
  if (keysA.size !== keysB.size) return false;

  // check that every key that is in 'a' is also in 'b'
  for (const key of keysA) if (!keysB.has(key)) return false;

  return true;
}

export function filterSet<T>(
  inputSet: Set<T>,
  predicate: (value: T, set: Set<T>) => boolean,
): Set<T> {
  const output = new Set<T>();
  for (const value of inputSet) {
    if (predicate(value, inputSet)) {
      output.add(value);
    }
  }

  return output;
}

export function getFileFAIcon(contentType: string) {
  const extension = contentType.split("/")[1];
  if (extension === "pdf") {
    return faFilePdf;
  }

  return faFile;
}

// pass path without leading slash
export function getFileOrFolderFromPath(
  structure: Folder | ApiFile,
  path: string,
): Folder | ApiFile | undefined {
  /* .replace() to remove leading and trailing "/"
   * .split() to split it up at all "/" and turn it into an array
   * .filter() to get rid of empty segments (so no "" elements)
   */
  const dirs = path
    .replace(/^\/+|\/+$/g, "")
    .split("/")
    .filter(Boolean);

  if (dirs[0] === structure.name) dirs.shift();

  if (dirs.length === 0) {
    return structure;
  }
  if (!("children" in structure)) return undefined;

  const [nextDir, ...rest] = dirs;
  for (const child of structure.children) {
    if (child.name === nextDir) {
      return getFileOrFolderFromPath(child, rest.join("/"));
    }
  }

  return undefined;
}

export function getFileFromBlobResponse(
  // eslint-disable-next-line max-len
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-empty-object-type
  response: AxiosResponse<unknown, any, {}>,
): File {
  const blob = response.data as unknown as Blob;
  const file = new File([blob], response.headers["x-real-name"], {
    type: blob.type || "application/pdf",
    lastModified: Date.now(),
  });
  return file;
}

export function downloadFile(file: File) {
  const url = URL.createObjectURL(file);
  const a = document.createElement("a");
  a.href = url;
  a.download = file.name;
  a.click();
  URL.revokeObjectURL(url);
  a.remove();
}

// eslint-disable-next-line max-len
// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-empty-object-type
export function downloadBlob(response: AxiosResponse<unknown, any, {}>) {
  // create blob from response
  const blob = new Blob([response.data as unknown as Blob]);

  const filename =
    response.headers["x-real-name"] ??
    (response.headers["content-disposition"] as string | undefined)?.match(
      /filename="?([^";\n]+)"?/,
    )?.[1];

  const url = URL.createObjectURL(blob);
  // create dummy 'a' element
  const a = document.createElement("a");
  // attach the blob to the href of the 'a' element
  a.href = url;

  a.download = filename;
  // click the dummy 'a' element to download the file
  a.click();
  // cleanup
  URL.revokeObjectURL(url);
  a.remove();
}

type FormatPhoneNumberArgs = {
  phoneNumber: string;
  isMobile: boolean;
};

export function formatPhoneNumber({
  phoneNumber,
  isMobile,
}: FormatPhoneNumberArgs): string {
  if (!/\+?[0-9]+[0-9]{10}/.test(phoneNumber)) {
    return phoneNumber;
  }

  const countryCode = phoneNumber.slice(0, -10);
  const parts = [countryCode];
  if (isMobile) {
    parts.push(phoneNumber.slice(-10, -7));
    parts.push(phoneNumber.slice(-7, -4));
    parts.push(phoneNumber.slice(-4));
  } else {
    parts.push(phoneNumber.slice(-10, -6));
    parts.push(phoneNumber.slice(-6, -4));
    parts.push(phoneNumber.slice(-4, -2));
    parts.push(phoneNumber.slice(-2));
  }

  return parts.join(" ");
}

export function removeWhitespace(value: string) {
  return value.replace(/\s+/g, "");
}

export function getDateFormatLocale(lang: LanguageLocaleEnum) {
  return lang === "de" ? de : lang === "it" ? it : enUS;
}

export function formatDateFromLang(date: Date, lang: LanguageLocaleEnum) {
  const opts: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  };

  return date.toLocaleDateString(lang, opts);
}

export function formatDateFromLangLong(date: Date, lang: LanguageLocaleEnum) {
  const opts: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "long",
    weekday: "long",
    year: "numeric",
  };

  return date.toLocaleDateString(lang, opts);
}

export function parseDateFromLang(
  date: string,
  lang: LanguageLocaleEnum,
): Date {
  let dateParts: string[];
  if (lang === "de") {
    dateParts = date.split(".");
  } else {
    dateParts = date.split("/");
  }
  return new Date(
    Number(dateParts[0]),
    Number(dateParts[1]) - 1,
    Number(dateParts[2]),
  );
}

export function validatePassword(
  password: string,
  policy: PasswordPolicy,
): Record<PasswordPolicyError, boolean> {
  return {
    too_short:
      policy.minLength !== undefined && password.length < policy.minLength,
    missing_lowercase_letter:
      policy.requiresLowerLetter !== undefined &&
      policy.requiresLowerLetter &&
      !/[a-z]/.test(password),
    missing_uppercase_letter:
      policy.requiresUpperLetter !== undefined &&
      policy.requiresUpperLetter &&
      !/[A-Z]/.test(password),
    missing_digit:
      policy.requiresDigit !== undefined &&
      policy.requiresDigit &&
      !/[0-9]/.test(password),
    missing_symbol:
      policy.requiresSymbol !== undefined &&
      policy.requiresSymbol &&
      !/\W/.test(password),
  };
}

// removes thousands separator, replaces floating point comma with point
// this allows one to parse a number entered with the european format
export function numberCommaToPoint(s: string) {
  return s.replaceAll(".", "").replace(",", ".");
}

export function unreachable(message: string): never {
  throw new Error(message);
}

export function langToLocale(lang: LanguageLocaleEnum): string {
  return lang === "en" ? "en-US" : lang;
}
